import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  DestroyRef,
  ElementRef,
  ViewEncapsulation,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';

import { SvgIconComponent } from '@dmb/core';

// Adapted from https://inclusive-components.design/tooltips-toggletips/

@Component({
  selector: 'dmb-help-button',
  templateUrl: './help-button.component.html',
  styleUrl: './help-button.component.scss',
  imports: [SvgIconComponent],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--status-offset]': 'statusOffset()',
    '(window:resize)': 'onResize($event.target)',
  },
})
export class HelpButtonComponent {
  private timeoutHandle: ReturnType<typeof setTimeout> | undefined = undefined;
  private hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  windowWidth = signal(inject(DOCUMENT).defaultView?.innerWidth);
  private rect = signal<{ left: number; right: number }>(this.hostRef.nativeElement.getBoundingClientRect(), {
    equal: (a, b) => a.left === b.left && a.right === b.right,
  });

  text = input.required<string>();
  displayText = signal(false);

  statusOffset = computed(() => {
    const { left, right } = this.rect();

    // Half width from css. Assumption: windowWidth is always bigger than status.
    const centerOffset = 200;

    if (left < centerOffset) {
      return `-${Math.max(0, left)}px`;
    }

    const windowWidth = this.windowWidth();
    if (windowWidth === undefined) {
      return undefined;
    }

    const distanceRight = windowWidth - right;
    if (distanceRight < centerOffset) {
      return `-${400 - distanceRight}px`;
    }

    return undefined;
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.timeoutHandle));
  }

  onResize(target: EventTarget | null) {
    if (target instanceof Window) {
      this.windowWidth.set(target.innerWidth);
    }
  }

  resetStatus() {
    clearTimeout(this.timeoutHandle);
    this.displayText.set(false);
  }

  click() {
    this.resetStatus();
    this.rect.set(this.hostRef.nativeElement.getBoundingClientRect());

    this.timeoutHandle = setTimeout(() => {
      this.displayText.set(true);
    }, 100);
  }

  keydown(evt: KeyboardEvent) {
    if (evt.key === 'Escape') {
      this.resetStatus();
    }
  }
}
